import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _get from "lodash/get";
var _excluded = ["headers", "serverRequest", "cache", "fetch", "log"];
import "regenerator-runtime/runtime.js";
import "core-js/modules/es6.array.filter.js";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.regexp.split.js";
import ApolloLinkTimeout from 'apollo-link-timeout';
import { setContext } from '@apollo/client/link/context';
import { getCoreHTTPHeaders } from '@nerdwallet/api-sdk-core';
import config from '@nerdwallet/app-config';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import defaultTypePolicies from '../type-policies';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, onError } from '../index';
import introspectionResult from '../generated-gql/possible-types';
import { fetch, REQUEST_TIMEOUT_MS } from './fetch';

/**
 * Returns the possibleTypes for fragments.
 * If one is provided in apolloOptions, that will be used.
 * Otherwise, one will be generated using the generated-gql possibleTypes.
 * To update the default data, run `npm run generate-possible-types`.
 */
export var getPossibleTypes = function getPossibleTypes(options) {
  return options.possibleTypes || introspectionResult.possibleTypes;
};
/**
 * Returns the typePolicies for fragments.
 * If one is provided in apolloOptions, that will be used.
 */

export var getTypePolicies = function getTypePolicies(options) {
  return options.typePolicies || defaultTypePolicies;
};
/**
 * Instantiates an apollo-client integration with query0.
 */

export default (function () {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var possibleTypes = getPossibleTypes(options);
  var typePolicies = getTypePolicies(options);

  var headers = options.headers,
      serverRequest = options.serverRequest,
      _options$cache = options.cache,
      cache = _options$cache === void 0 ? new InMemoryCache({
    possibleTypes: possibleTypes,
    typePolicies: typePolicies
  }) : _options$cache,
      fetchOption = options.fetch,
      _options$log = options.log,
      log = _options$log === void 0 ? function () {} : _options$log,
      apolloOptions = _objectWithoutProperties(options, _excluded);

  var linkOptions = {
    uri: config.API.BASE_URLS.QUERY0,
    // Use `credentials: include` to allow for cross-origin requests to
    // include cookies. For example, when making a cross-origin request
    // from localnerd.nerdwallet.com to api.nerdwallet.biz, this allows
    // requests to include cookies set for api.nerdwallet.biz. Without
    // `credentials: include`, no cookies would be sent. This allows
    // local development against stage or prod query0.
    credentials: 'include',
    headers: _objectSpread(_objectSpread({}, headers), {}, {
      // Since this code is designed to be isomorphic, we'll set a
      // different platform ID depending on if we're executing on a
      // server (SSR) or on a client. Note that query0 will refuse
      // requests without a valid client ID specified in this header.
      'X-Client-Platform': serverRequest ? 'server' : 'web'
    }),
    fetch: fetchOption || fetch
  };
  var timeoutLinkForHttp = new ApolloLinkTimeout(config.REQUEST_TIMEOUT_MS || REQUEST_TIMEOUT_MS);
  var timeoutLinkForBatch = new ApolloLinkTimeout(config.REQUEST_TIMEOUT_MS || REQUEST_TIMEOUT_MS);
  var batchLink = new BatchHttpLink(linkOptions);
  var timeoutBatchLink = timeoutLinkForBatch.concat(batchLink);
  var singularLink = new HttpLink(linkOptions);
  var timeoutHttpLink = timeoutLinkForHttp.concat(singularLink);
  return new ApolloClient(_objectSpread(_objectSpread({}, apolloOptions), {}, {
    cache: cache,
    // This enables us to know things such as which clients call deprecated APIs
    name: config.CLIENT_ID,
    version: _get(config, '_meta.version'),
    link: ApolloLink.from([onError(function (_ref) {
      var graphQLErrors = _ref.graphQLErrors,
          networkError = _ref.networkError;

      if (graphQLErrors) {
        graphQLErrors.map(function (_ref2) {
          var message = _ref2.message,
              locations = _ref2.locations,
              path = _ref2.path;
          return log("[GraphQL error]: Message: ".concat(message, ", Location: ").concat(locations, ", Path: ").concat(path));
        });
      }

      if (networkError) log("[Network error]: ".concat(networkError));
    }), // Avoid outdated header tokens by refetching headers asynchronously on
    // request to prevent stale data from cached security tokens. These will
    // get set as context.headers which is used by apollo-link-http:
    // https://github.com/apollographql/apollo-link/blob/a204a87e9e8c2ff9d4f797e1b17b843078aa5622/packages/apollo-link-http/src/httpLink.ts#L84
    // https://nerdwallet.atlassian.net/browse/FEI-847
    setContext( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(previousContext, _ref3) {
        var operationHeaders, coreHeaders;
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                operationHeaders = _ref3.headers;
                _context.next = 3;
                return getCoreHTTPHeaders({
                  serverRequest: serverRequest,
                  config: {
                    applicationID: config.APPLICATION_ID,
                    clientID: config.CLIENT_ID
                  }
                });

              case 3:
                coreHeaders = _context.sent;
                return _context.abrupt("return", {
                  headers: _objectSpread(_objectSpread({}, operationHeaders), coreHeaders)
                });

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2) {
        return _ref4.apply(this, arguments);
      };
    }()), ApolloLink.split(function (operation) {
      var _operation$getContext;

      return ((_operation$getContext = operation.getContext()) === null || _operation$getContext === void 0 ? void 0 : _operation$getContext.shouldBatch) === true;
    }, timeoutBatchLink, timeoutHttpLink)].filter(Boolean))
  }));
});